// LN Pro · Instagram actions producer (ACTIONS adapter #2)
//
// PKT-0633's consumer-side seam. The desk renders ActionCards; this is where
// they come from. Deliberately mirrors redditAnswerService's shape and its
// waking/unavailable boundary, because the Workbench behind BOTH surfaces is
// the same cold-starting Render service and the honest distinction between
// "still warming" and "actually broken" is the same distinction.
//
// 🔴 WHY AN EMPTY QUEUE IS NOT JUST AN EMPTY QUEUE
// This surface can be empty for two completely different reasons, and a desk
// that renders them identically teaches the operator the wrong thing:
//
//   "nothing scored high enough today"        → the system worked, come back later
//   "the follower roster is unverified"       → the system CANNOT rank yet, because
//                                               every influencer→LocateNow edge is
//                                               `unknown` until PKT-0632 completes
//
// The second is the state we are actually in today: the roster stands at 170 of
// 565 observed and no credentials exist on the executing seat, so nothing can
// be verified. An empty list with a shrug would misreport that as "no
// opportunities". `unavailable_because` carries the real reason to the UI.

import { ApiError, fetchWithAuth, TimeoutError } from "../lib/api";
import type { ActionQueue, ActionQueueState, ActionCard } from "./actionsContract";
import { instagramOpportunityToCard, type InstagramOpportunity } from "./actionAdapters";

/** What the backend returns, before adaptation. */
export interface InstagramQueueResponse {
  items: InstagramOpportunity[];
  counts: Record<ActionQueueState, number>;
  /**
   * Present when the producer cannot rank rather than merely having nothing to
   * show. Null when the queue is legitimately empty.
   */
  unavailable_because: string | null;
  /** Roster coverage behind the ranking, so the desk can show its own limits. */
  roster_coverage: { observed: number; total: number; complete: boolean; observedAt: string } | null;
  posting_enabled: false;
}

export interface InstagramActionQueue extends ActionQueue {
  unavailable_because: string | null;
  roster_coverage: InstagramQueueResponse["roster_coverage"];
}

/**
 * Is this failure the Workbench still warming up, rather than a real outage?
 *
 * THE BOUNDARY — exactly two things count as "waking", and nothing else. This
 * is copied in SUBSTANCE, not by import, from isRedditAnswerWakingError, whose
 * own comment records why widening it is a trap: a real outage rendering as
 * "waking up, try again" is no more honest than a cold start rendering as
 * "unavailable". Kept separate so the Instagram route's own server message can
 * diverge without silently changing Reddit's behaviour.
 */
export function isInstagramActionsWakingError(error: unknown): boolean {
  if (error instanceof TimeoutError) return true;
  const detail =
    error instanceof ApiError && error.body && typeof error.body === "object"
      ? String((error.body as { detail?: unknown }).detail ?? "")
      : "";
  return detail === "Instagram actions desk is waking";
}

/**
 * Adapt the backend payload into the shared contract.
 *
 * Every card is built through instagramOpportunityToCard, so the three-valued
 * edge state and the null suggested_text arrive already correct — the desk
 * never sees a raw opportunity and cannot accidentally read an `unknown` edge
 * as a verified one.
 */
export function toActionQueue(response: InstagramQueueResponse): InstagramActionQueue {
  const items: ActionCard[] = response.items.map(instagramOpportunityToCard);
  return {
    items,
    counts: response.counts,
    posting_enabled: false,
    unavailable_because: response.unavailable_because,
    roster_coverage: response.roster_coverage,
  };
}

/**
 * The sentence the desk shows when there is nothing to act on.
 *
 * Takes the queue, not a boolean, because the interesting cases are all about
 * WHY it is empty and a boolean throws that away.
 */
export function emptyStateReason(queue: InstagramActionQueue): string | null {
  if (queue.items.length > 0) return null;
  if (queue.unavailable_because) return queue.unavailable_because;
  const coverage = queue.roster_coverage;
  if (coverage && !coverage.complete) {
    const pct = ((coverage.observed / Math.max(coverage.total, 1)) * 100).toFixed(1);
    return (
      `No ranked actions: the follower roster is ${coverage.observed} of ${coverage.total} observed ` +
      `(${pct}%), so every influencer→LocateNow edge is still UNKNOWN. This is not "no opportunities" — ` +
      `it is "cannot rank yet". Completing the roster (PKT-0632) is what turns unknown into verified.`
    );
  }
  return "Nothing in the Instagram queue right now.";
}

export interface InstagramActionReceipt {
  recorded: true;
  opportunity_id: string;
  action: "marked_done" | "dismissed";
}

export class InstagramReceiptUnconfirmedError extends Error {
  constructor() {
    super("Receipt outcome unconfirmed. Check the action store before retrying.");
    this.name = "InstagramReceiptUnconfirmedError";
  }
}

export function confirmInstagramReceipt(value: unknown, id: string, action: InstagramActionReceipt["action"]): InstagramActionReceipt {
  const receipt = value as Partial<InstagramActionReceipt> | null;
  if (!receipt || receipt.recorded !== true || receipt.opportunity_id !== id || receipt.action !== action) {
    throw new InstagramReceiptUnconfirmedError();
  }
  return { recorded: true, opportunity_id: id, action };
}

export const instagramActionsService = {
  list: async (state: ActionQueueState): Promise<InstagramActionQueue> =>
    toActionQueue(
      await fetchWithAuth<InstagramQueueResponse>(`/admin/content/instagram/queue?state=${state}`),
    ),
  /**
   * Records what a HUMAN did. There is no executor and there is not meant to
   * be one: no like, follow, comment, DM or scheduled action. Meta enforces on
   * this, and the standing design is human-acts-system-records.
   */
  action: async (
    id: string,
    body: { action: "marked_done" | "dismissed"; dismiss_reason?: string | null },
  ): Promise<InstagramActionReceipt> => {
    if (!/^[A-Za-z0-9._-]{1,160}$/.test(id) || id === "." || id === "..") throw new Error("Invalid Instagram opportunity");
    if (body.action !== "marked_done" && body.action !== "dismissed") throw new Error("Only human receipts are accepted");
    if (body.action === "dismissed" && !body.dismiss_reason?.trim()) throw new Error("Dismiss reason is required");
    try {
      const response = await fetchWithAuth<unknown>(
        `/admin/content/instagram/${encodeURIComponent(id)}/action`,
        { method: "POST", body: JSON.stringify(body), retry: false, authRetry: false },
      );
      return confirmInstagramReceipt(response, id, body.action);
    } catch (error) {
      const recorded = error instanceof ApiError && error.body && typeof error.body === "object"
        ? (error.body as { recorded?: unknown }).recorded : undefined;
      if (recorded === false || (recorded === undefined && error instanceof ApiError && [400, 401, 403, 404, 422, 429].includes(error.status))) {
        throw new Error("This receipt was NOT recorded. Check the request or connection before trying again."); // A definite refusal may be corrected, unlike an uncertain write.
      }
      throw new InstagramReceiptUnconfirmedError();
    }
  },
};
